/**
 * Public profile view – avatar, name, bio, stats, lift history (read-only).
 */
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProfile } from '../../domain/usecases/profile';
import { getUserWorkouts } from '../../domain/usecases/workouts';
import { DISCIPLINE_LABELS } from '../../domain/entities/User';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import Button from '../../shared/ui/Button.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import styles from './ProfileViewPage.module.css';

export function ProfileViewPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    Promise.all([getProfile(userId), getUserWorkouts(userId, 50)])
      .then(([p, w]) => {
        if (!cancelled) {
          setProfile(p);
          setWorkouts(w);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton width="100%" height={160} radius="var(--radius-md)" style={{ marginBottom: '1rem' }} />
        <Skeleton width="60%" height={24} radius="var(--radius-sm)" style={{ marginBottom: '0.5rem' }} />
        <Skeleton width="40%" height={16} radius="var(--radius-sm)" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={styles.page}>
        <EmptyState icon="user" title="Profile not found" body="This athlete does not exist." />
      </div>
    );
  }

  const prs = profile.stats || {};

  return (
    <div className={styles.page}>
      {/* Header card */}
      <Card style={{ marginBottom: '1.25rem', textAlign: 'center' }}>
        <Avatar
          src={profile.avatarUrl}
          initials={profile.displayName?.charAt(0)?.toUpperCase() || 'A'}
          size="lg"
          style={{ margin: '0 auto 0.75rem' }}
        />
        <Display as="h1" size="l" style={{ marginBottom: '0.2rem' }}>
          {profile.displayName || 'Athlete'}
        </Display>
        {profile.bio && (
          <Body tone="secondary" style={{ marginBottom: '0.4rem' }}>{profile.bio}</Body>
        )}
        {(() => {
          const parts = [];
          if (profile.nationality) parts.push(profile.nationality);
          if (profile.age) parts.push(`${profile.age} yrs`);
          if (profile.bodyweight != null) parts.push(`${profile.bodyweight} kg`);
          if (profile.discipline) parts.push(DISCIPLINE_LABELS[profile.discipline] || profile.discipline);
          if (profile.federation) parts.push(profile.federation);
          return parts.length > 0 ? (
            <Body tone="secondary" style={{ marginBottom: '0.75rem' }}>{parts.join(' · ')}</Body>
          ) : null;
        })()}
        <Button
          variant="secondary"
          as={Link}
          to={`/compare/${userId}`}
        >
          Compare with me
        </Button>
      </Card>

      {/* PRs section */}
      <section className={styles.section}>
        <Title as="h2" style={{ marginBottom: '0.75rem' }}>PRs</Title>
        {(prs.squatPr != null || prs.benchPr != null || prs.deadliftPr != null) ? (
          <div className={styles.statsRow}>
            {prs.squatPr != null && <Stat label="Squat" value={`${prs.squatPr} kg`} />}
            {prs.benchPr != null && <Stat label="Bench" value={`${prs.benchPr} kg`} />}
            {prs.deadliftPr != null && <Stat label="Deadlift" value={`${prs.deadliftPr} kg`} />}
          </div>
        ) : (
          <Body tone="secondary">No PRs yet.</Body>
        )}
      </section>

      {/* Lift history (read-only) */}
      <section className={styles.section}>
        <Title as="h2" style={{ marginBottom: '0.75rem' }}>Lift history</Title>
        {workouts.length === 0 ? (
          <EmptyState
            icon="barbell"
            title="No lifts yet"
            body="This athlete hasn't logged any lifts."
          />
        ) : (
          <ul className={styles.workoutList}>
            {workouts.slice(0, 20).map((w) => (
              <li key={w.id} className={styles.workoutItem}>
                <Body as="span">{w.exercise} · {w.weight} kg × {w.reps}</Body>
                {w.videoUrl && (
                  <video src={w.videoUrl} className={styles.workoutVideo} muted playsInline />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
